<template>
  <div
    v-if="currentUser"
    class="user-menu v-menu"
  >
    <el-header-menu
      icon="i-user-circle-o--24"
      :items="userMenuItems"
      :title="currentUser.name"
      width="256px"
    />
    <about
      v-if="aboutDialogVisible"
      @close="aboutDialogVisible = false"
    />
    <userChangePassword
      v-if="passwordDialogVisible"
      @close="passwordDialogVisible = false"
    />
    <userChangeLanguage
      v-if="languageDialogVisible"
      @close="languageDialogVisible = false"
    />
    <problemReport
      v-if="problemReportDialogVisible"
      @close="problemReportDialogVisible = false"
    />
    <twoFactorAuthDialog
      v-if="twoFactorDialogVisible"
      @close="twoFactorDialogVisible = false"
    />
    <disableTwoFactorAuthDialog
      v-if="disableTwoFactorDialogVisible"
      :logout-after-disable="logoutAfterTwoFactorReset"
      @close="disableTwoFactorDialogVisible = false"
    />
    <slot name="dialogs" />
  </div>
</template>

<script>

  import { mapState, mapStores } from 'pinia';
  import useAuthStore from '@/common/store/pinia/auth';
  import useTasksStore from '@/common/store/pinia/tasks';
  import useTotpStore from '@/common/store/pinia/totp';
  import useTotpSettingsStore from '@/common/store/pinia/totpSettings';
  import takeFirst from '@/common/utils/takeFirst';
  import UserChangePassword from './ChangePassword.vue';
  import UserChangeLanguage from './ChangeLanguage.vue';
  import ProblemReport from './ProblemReport.vue';
  import About from './About.vue';
  import TwoFactorAuthDialog from './TwoFactorAuthDialog.vue';
  import DisableTwoFactorAuthDialog from './DisableTwoFactorAuthDialog.vue';

  export default {
    name: 'UserMenu',
    components: {
      UserChangePassword,
      UserChangeLanguage,
      About,
      ProblemReport,
      TwoFactorAuthDialog,
      DisableTwoFactorAuthDialog
    },
    data() {
      return {
        passwordDialogVisible: false,
        languageDialogVisible: false,
        aboutDialogVisible: false,
        problemReportDialogVisible: false,
        twoFactorDialogVisible: false,
        disableTwoFactorDialogVisible: false,
        logoutAfterTwoFactorReset: true
      };
    },
    mounted() {
      if (!this.totpStore.status) {
        this.fetchTotpStatus();
      }
    },
    computed: {
      ...mapStores(useAuthStore, useTasksStore, useTotpStore, useTotpSettingsStore),
      ...mapState(useAuthStore, ['currentUser', 'currentIdpId']),
      ...mapState(useTotpStore, {
        totpLoading: 'loading',
        isTotpEnabled: 'isTotpEnabled'
      }),
      isComputeConfigured() {
        return this.$store.getters['compute/isComputeConfigured'];
      },
      sendingReportDisabled() {
        return !!this.tasksStore.reportingProblemTask;
      },
      twoFactorMenuTitle() {
        return this.isTotpEnabled
          ? this.$t('Reset 2FA')
          : this.$t('Configure 2FA');
      },
      // Hiding the entry must never lock out a user who has to reset or use 2FA.
      isTwoFactorMenuItemVisible() {
        return this.totpSettingsStore.isTotpUserSetupEnabled
          || this.totpStore.isTotpEnabled
          || this.totpStore.isTotpEnforced;
      },
      userMenuItems() {
        const isFederated = !!this.currentIdpId;
        return [
          {
            title: this.$t('Report a problem'),
            right: true,
            disabled: this.sendingReportDisabled,
            action: () => this.showProblemReportDialog()
          },
          {
            title: this.$t('Language'),
            right: true,
            icon: 'root-o',
            divided: isFederated,
            action: () => this.showLanguageDialog()
          },
          ...(isFederated ? [] : [{
            title: this.$t('Change password'),
            divided: true,
            action: () => this.showPasswordDialog()
          }]),
          {
            title: this.twoFactorMenuTitle,
            right: true,
            action: () => {
              if (this.totpStore.isTotpEnabled) {
                return this.showDisableTwoFactorAuthDialog();
              }
              return this.showTwoFactorAuthDialog();
            },
            class: 'two-factor-auth',
            disabled: this.totpLoading,
            visible: !isFederated && this.isTwoFactorMenuItemVisible,
          },
          {
            title: this.$t('API credentials'),
            action: () => this.$router.push('/settings/api-access/credentials'),
            visible: this.isComputeConfigured,
          },
          {
            title: this.$t('About'),
            right: true,
            action: () => this.showAbout(),
            class: 'about',
            divided: true
          },
          {
            title: this.$t('Log out'),
            class: 'log-out',
            action: () => this.authStore.signOut()
          },
        ];
      }
    },
    methods: {
      fetchTotpStatus() {
        return takeFirst(this.totpStore.fetchStatus);
      },
      showLanguageDialog() {
        this.languageDialogVisible = true;
      },
      showPasswordDialog() {
        this.passwordDialogVisible = true;
      },
      showAbout() {
        this.aboutDialogVisible = true;
      },
      showProblemReportDialog() {
        this.problemReportDialogVisible = true;
      },
      showTwoFactorAuthDialog() {
        this.twoFactorDialogVisible = true;
      },
      showDisableTwoFactorAuthDialog() {
        this.disableTwoFactorDialogVisible = true;
      },
    }
  };
</script>

<style lang="scss" scoped>
::v-deep .el-header__menu {
  display: block;
}
</style>
